import { Component, Inject, Optional, ChangeDetectionStrategy } from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../reducers';
import { Observable, combineLatest } from 'rxjs';
import { take } from 'rxjs/operators';
import { AnalyticsService } from 'spotlight-ui/app/core/analytics';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CareConstants } from '../care.constants';
import { ServiceNowCase } from '../../models/recover/ServiceNowCase';
import * as care from '../../actions/care';
import { CareService } from '../care.service';
import { ReassignInputDialogComponent } from '../reassign-input-dialog/reassign-input-dialog.component';

@Component({
  selector: 'service-now-report',
  templateUrl: './service-now-report.component.html',
  styleUrls: ['./service-now-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ServiceNowReportComponent {
  public isMobile$: Observable<boolean>;
  public readOnlyUser$: Observable<boolean>;
  public serviceNowCase$: Observable<ServiceNowCase>;
  public selectedReport$: Observable<any>;
  public serviceNowCaseError$: Observable<string>;
  public serviceNowCaseLoading$: Observable<boolean>;
  public sendWithOffer = CareConstants.withCareOffer;
  public noEmailText = CareConstants.noEmilText;

  constructor(private analytics: AnalyticsService,
    private dialog: MatDialog,
    private store: Store<fromRoot.State>,
    private router: Router,
    private careService: CareService,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any,
    public dialogRef: MatDialogRef<ServiceNowReportComponent>) {
    this.isMobile$ = this.store.select(fromRoot.getIsMobile);
    this.readOnlyUser$ = this.store.select(fromRoot.getReadOnlyUser);
    this.serviceNowCase$ = this.store.select(fromRoot.getCareServiceNowCase);
    this.selectedReport$ = store.select(fromRoot.getSelectedCareReport);
    this.serviceNowCaseError$ = this.store.select(fromRoot.getCareServiceNowCaseError);
    this.serviceNowCaseLoading$ = this.store.select(fromRoot.getCareServiceNowCaseLoading);
  }

  public closeReport() {
    this.dialogRef.close();
  }

  public acknowledgeReport() {
    this.serviceNowCase$.pipe(take(1)).subscribe((serviceNowCase) => {
      this.analytics.sendEvent(
        'Report Acknowledged',
        { id: serviceNowCase.id, number: serviceNowCase.number }
      );
      this.store.dispatch(new care.AcknowledgeReport());
    });
  }

  public goToReassignDialog() {

    combineLatest([
      this.serviceNowCase$,
      this.isMobile$
    ]).pipe(take(1)).subscribe(([serviceNowCase, isMobile]) => {
      const reassignDialogRef = this.dialog.open(ReassignInputDialogComponent, {
        width: isMobile ? '90vw' : '500px',
        data: {
          name: serviceNowCase.number,
          isMobile: isMobile
        }
      });

      reassignDialogRef.afterClosed().subscribe(result => {
        if (result) {
          const payload = {
            reassignReason: result.reassignReason,
            isCustomerContacted: result.isCustomerContacted,
            reassignLocation: result.reassignLocation
          };

          this.store.dispatch(new care.ReassignReport(payload));

          this.closeReport();

          this.analytics.sendEvent(
            'Report Reassigned',
            {
              number: serviceNowCase.number,
              ...payload
            }
          );
        }
      });
    });
  }

  public goToActionSelection = (): void => {
    this.closeReport();
    this.store.dispatch(new care.CloseAllOpenNotes());

    this.isMobile$.pipe(take(1)).subscribe((isMobile) => {
      this.selectedReport$.pipe(take(1)).subscribe((report) => {
        if (isMobile) {
          this.router.navigate(
            ['/care/action-selection'],
            { queryParams: { incidentId: report.id } }
          );
        } else {
          this.careService.openActionSelectionDialog(report, this.sendWithOffer, true);
        }
      });
    });
  }

  public reopenReport = (): void => {
    this.closeReport();
    this.store.dispatch(new care.ReopenReport());
  }
}
